import { CheckCircle, Edit, Plus, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import Sidebar from '../components/layout/Sidebar';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    h1 {
      font-size: ${theme.typography.fontSize['2xl']};
    }
  }
`;

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const TableCard = styled(Card)`
  padding: ${theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  border: 3px solid ${({ status }) => {
    if (status === 'occupied') return theme.colors.status.preparing;
    if (status === 'reserved') return theme.colors.status.pending;
    return 'transparent';
  }};
  background-color: ${({ status }) => {
    if (status === 'occupied') return `${theme.colors.status.preparing}10`;
    if (status === 'reserved') return `${theme.colors.status.pending}10`;
    return theme.colors.white;
  }};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const TableNumber = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.text};
`;

const TableCapacity = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ status }) => {
    if (status === 'occupied') return `${theme.colors.status.preparing}20`;
    if (status === 'reserved') return `${theme.colors.status.pending}20`;
    return `${theme.colors.status.ready}20`;
  }};
  color: ${({ status }) => {
    if (status === 'occupied') return theme.colors.status.preparing;
    if (status === 'reserved') return theme.colors.status.pending;
    return theme.colors.status.ready;
  }};
  margin-bottom: ${theme.spacing.md};
`;

const TableActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: center;
  margin-top: ${theme.spacing.md};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 500px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

// Full tables data as per requirements
const initialTables = [
  // Indian Baithak 1-7
  ...Array.from({ length: 7 }, (_, i) => ({
    id: `baithak${i+1}`,
    number: i + 1,
    name: `Indian Baithak ${i+1}`,
    section: 'Indian Baithak',
    capacity: 4,
    status: 'available',
    location: 'Baithak',
  })),
  // Courtyard 1-8
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `courtyard${i+1}`,
    number: i + 1,
    name: `Courtyard ${i+1}`,
    section: 'Courtyard',
    capacity: 4,
    status: 'available',
    location: 'Courtyard',
  })),
  // Dinning 1-20
  ...Array.from({ length: 20 }, (_, i) => ({
    id: `dinning${i+1}`,
    number: i + 1,
    name: `Dinning ${i+1}`,
    section: 'Dinning',
    capacity: 4,
    status: 'available',
    location: 'Dinning',
  })),
  // Garden 1-10
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `garden${i+1}`,
    number: i + 1,
    name: `Garden ${i+1}`,
    section: 'Garden',
    capacity: 4,
    status: 'available',
    location: 'Garden',
  })),
  // AC Hall 1-8
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `achall${i+1}`,
    number: i + 1,
    name: `AC Hall ${i+1}`,
    section: 'AC Hall',
    capacity: 4,
    status: 'available',
    location: 'AC Hall',
  })),
];

function TablesManagement() {
  const [tables, setTables] = useState(initialTables);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [formData, setFormData] = useState({
    number: '',
    capacity: '',
    status: 'available',
    location: '',
  });

  const handleAddTable = () => {
    setEditingTable(null);
    setFormData({
      number: '',
      capacity: '',
      status: 'available',
      location: '',
    });
    setShowModal(true);
  };

  const handleEdit = (table) => {
    setEditingTable(table);
    setFormData({
      number: table.number,
      capacity: table.capacity,
      status: table.status,
      location: table.location,
    });
    setShowModal(true);
  };

  const handleDelete = (tableId) => {
    if (window.confirm('Are you sure you want to delete this table?')) {
      setTables((prev) => prev.filter((table) => table.id !== tableId));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingTable) {
      setTables((prev) =>
        prev.map((table) =>
          table.id === editingTable.id
            ? {
                ...table,
                ...formData,
                number: parseInt(formData.number),
                capacity: parseInt(formData.capacity),
              }
            : table
        )
      );
    } else {
      const newTable = {
        id: Date.now(),
        ...formData,
        number: parseInt(formData.number),
        capacity: parseInt(formData.capacity),
      };
      setTables((prev) => [...prev, newTable]);
    }
    setShowModal(false);
    setFormData({
      number: '',
      capacity: '',
      status: 'available',
      location: '',
    });
  };

  const handleStatusChange = (tableId, newStatus) => {
    setTables((prev) =>
      prev.map((table) =>
        table.id === tableId ? { ...table, status: newStatus } : table
      )
    );
  };

  const filteredTables = tables.filter((table) =>
    table.number.toString().includes(searchTerm) ||
    table.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Tables Management</h1>
          <Button variant="primary" onClick={handleAddTable}>
            <Plus size={20} />
            Add Table
          </Button>
        </Header>

        <ActionsBar>
          <Input
            type="text"
            placeholder="Search tables..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, maxWidth: '400px' }}
          />
        </ActionsBar>

        <TablesGrid>
          {filteredTables.length === 0 ? (
            <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing['3xl'] }}>
              <p style={{ color: theme.colors.textSecondary }}>No tables found</p>
            </Card>
          ) : (
            filteredTables.map((table) => (
              <TableCard key={table.id} status={table.status}>
                <TableNumber>Table {table.number}</TableNumber>
                <TableCapacity>Capacity: {table.capacity} guests</TableCapacity>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>
                  {table.location}
                </p>
                <StatusBadge status={table.status}>
                  {table.status === 'available' && <CheckCircle size={14} />}
                  {table.status === 'occupied' && <XCircle size={14} />}
                  {table.status === 'reserved' && <XCircle size={14} />}
                  {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                </StatusBadge>
                <TableActions>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(table)}
                  >
                    <Edit size={16} />
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDelete(table.id)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </TableActions>
              </TableCard>
            ))
          )}
        </TablesGrid>

        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>{editingTable ? 'Edit Table' : 'Add Table'}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalForm onSubmit={handleSubmit}>
                <InputGroup>
                  <label>Table Number</label>
                  <Input
                    type="number"
                    value={formData.number}
                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Capacity (guests)</label>
                  <Input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Location</label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Window, Corner, Center, etc."
                    required
                  />
                </InputGroup>
                <InputGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    <option value="available">Available</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                  </select>
                </InputGroup>
                <ButtonGroup>
                  <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    {editingTable ? 'Update' : 'Add'} Table
                  </Button>
                </ButtonGroup>
              </ModalForm>
            </ModalContent>
          </Modal>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default TablesManagement;

